// ======================================================================
// FILE:
// src/modules/finance/reports/general_ledger/pages/GeneralLedgerPage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// General Ledger
//
// PURPOSE:
// Read-only General Ledger inquiry.
//
// FEATURES:
// - Company filter
// - Financial Year filter
// - Financial Period filter
// - Account filter
// - Date range
// - Summary KPIs
// - General Ledger summary
// - Account Ledger detail
// - Running balance
// - Responsive layout
// ======================================================================

import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import AsyncSelect from "react-select/async";

import FinancePageHeader
    from "../../../components/FinancePageHeader";

import FinanceSummaryCards
    from "../../../components/FinanceSummaryCards";

import FinanceSearchPanel
    from "../../../components/FinanceSearchPanel";

import generalLedgerService
    from "../../../services/generalLedgerService";

import financeLookupService
    from "../../../services/financeLookupService";


// ======================================================================
// COMPONENT
// ======================================================================

export default function GeneralLedgerPage() {

    // ==============================================================
    // LOADING
    // ==============================================================

    const [
        loading,
        setLoading,
    ] = useState(false);


    // ==============================================================
    // DATA
    // ==============================================================

    const [
        rows,
        setRows,
    ] = useState([]);


    const [
        summary,
        setSummary,
    ] = useState({
        opening_balance: 0,
        total_debit: 0,
        total_credit: 0,
        closing_balance: 0,
    });


    const [
        errorMessage,
        setErrorMessage,
    ] = useState("");


    // ==============================================================
    // VIEW MODE
    // ==============================================================

    const [
        viewMode,
        setViewMode,
    ] = useState("SUMMARY");


    // ==============================================================
    // SELECTED LOOKUPS
    // ==============================================================

    const [
        selectedCompany,
        setSelectedCompany,
    ] = useState(null);


    const [
        selectedFinancialYear,
        setSelectedFinancialYear,
    ] = useState(null);


    const [
        selectedFinancialPeriod,
        setSelectedFinancialPeriod,
    ] = useState(null);


    const [
        selectedAccount,
        setSelectedAccount,
    ] = useState(null);


    // ==============================================================
    // FILTERS
    // ==============================================================

    const [
        filters,
        setFilters,
    ] = useState({

        company_id: null,

        financial_year_id: null,

        financial_period_id: null,

        account_id: null,

        date_from: "",

        date_to: "",

    });


    // ==============================================================
    // UPDATE FILTER
    // ==============================================================

    const updateFilter = useCallback(
        (
            field,
            value
        ) => {

            setFilters(
                previous => ({
                    ...previous,
                    [field]: value,
                })
            );

        },
        []
    );


    // ==============================================================
    // COMPANY LOOKUP
    // ==============================================================

    const loadCompanies = async (
        inputValue = ""
    ) => {

        try {

            const response =
                await financeLookupService.searchCompanies(
                    inputValue
                );

            return (
                response || []
            ).map(
                company => ({

                    value:
                        company.id,

                    label:
                        company.company_name ||
                        company.name ||
                        `Company ${company.id}`,

                    data:
                        company,

                })
            );

        } catch (error) {

            console.error(
                "Failed to load companies:",
                error
            );

            return [];

        }

    };


    // ==============================================================
    // FINANCIAL YEAR LOOKUP
    // ==============================================================

    const loadFinancialYears = async (
        inputValue = ""
    ) => {

        try {

            const response =
                await financeLookupService.searchFinancialYears(
                    inputValue
                );

            return (
                response || []
            ).map(
                year => ({

                    value:
                        year.id,

                    label:
                        year.year_name ||
                        year.name ||
                        `Financial Year ${year.id}`,

                    data:
                        year,

                })
            );

        } catch (error) {

            console.error(
                "Failed to load financial years:",
                error
            );

            return [];

        }

    };


    // ==============================================================
    // FINANCIAL PERIOD LOOKUP
    // ==============================================================

    const loadFinancialPeriods = async (
        inputValue = ""
    ) => {

        if (
            !filters.financial_year_id
        ) {

            return [];

        }


        try {

            const response =
                await financeLookupService.searchFinancialPeriods(
                    filters.financial_year_id,
                    inputValue
                );

            return (
                response || []
            ).map(
                period => ({

                    value:
                        period.id,

                    label:
                        period.period_name ||
                        period.period_code ||
                        `Period ${period.id}`,

                    data:
                        period,

                })
            );

        } catch (error) {

            console.error(
                "Failed to load financial periods:",
                error
            );

            return [];

        }

    };


    // ==============================================================
    // ACCOUNT LOOKUP
    // ==============================================================

    const loadAccounts = async (
        inputValue = ""
    ) => {

        if (
            !filters.company_id
        ) {

            return [];

        }


        try {

            return await financeLookupService
                .searchChartOfAccounts(
                    filters.company_id,
                    inputValue,
                    null,
                    20
                );

        } catch (error) {

            console.error(
                "Failed to load chart of accounts:",
                error
            );

            return [];

        }

    };


    // ==============================================================
    // LOAD GENERAL LEDGER
    // ==============================================================

    const loadGeneralLedger = useCallback(
        async (
            appliedFilters = filters
        ) => {

            setLoading(true);

            setErrorMessage("");

            try {

                // ======================================================
                // ACCOUNT SELECTED
                // ======================================================
                //
                // Account selected means:
                //
                // GET /api/finance/general-ledger/account
                //
                // Response:
                // opening_balance
                // total_debit
                // total_credit
                // closing_balance
                // transactions[]
                // ======================================================

                if (
                    appliedFilters.account_id
                ) {

                    const response =
                        await generalLedgerService
                            .getAccountLedger(
                                appliedFilters
                            );


                    setViewMode(
                        "ACCOUNT"
                    );


                    setRows(
                        response?.transactions || []
                    );


                    setSummary({

                        opening_balance:
                            Number(
                                response?.opening_balance || 0
                            ),

                        total_debit:
                            Number(
                                response?.total_debit || 0
                            ),

                        total_credit:
                            Number(
                                response?.total_credit || 0
                            ),

                        closing_balance:
                            Number(
                                response?.closing_balance || 0
                            ),

                    });


                    return;

                }


                // ======================================================
                // NO ACCOUNT SELECTED
                // ======================================================
                //
                // GET /api/finance/general-ledger
                //
                // Response:
                // accounts[]
                // account_count
                // total_debit
                // total_credit
                // is_balanced
                // ======================================================

                const response =
                    await generalLedgerService
                        .getGeneralLedger(
                            appliedFilters
                        );


                const accounts =
                    response?.accounts || [];


                setViewMode(
                    "SUMMARY"
                );


                setRows(
                    accounts
                );


                // ------------------------------------------------------
                // Calculate summary opening/closing
                // ------------------------------------------------------

                const totalOpening =
                    accounts.reduce(
                        (
                            total,
                            item
                        ) =>
                            total +
                            Number(
                                item.opening_balance || 0
                            ),
                        0
                    );


                const totalClosing =
                    accounts.reduce(
                        (
                            total,
                            item
                        ) =>
                            total +
                            Number(
                                item.closing_balance || 0
                            ),
                        0
                    );


                setSummary({

                    opening_balance:
                        totalOpening,

                    total_debit:
                        Number(
                            response?.total_debit || 0
                        ),

                    total_credit:
                        Number(
                            response?.total_credit || 0
                        ),

                    closing_balance:
                        totalClosing,

                });

            } catch (error) {

                console.error(
                    "Failed to load General Ledger:",
                    error
                );


                setRows([]);


                setSummary({

                    opening_balance: 0,

                    total_debit: 0,

                    total_credit: 0,

                    closing_balance: 0,

                });


                setErrorMessage(
                    error?.response?.data?.message ||
                    "Unable to load General Ledger."
                );

            } finally {

                setLoading(false);

            }

        },
        [filters]
    );


    // ==============================================================
    // INITIAL LOAD
    // ==============================================================

    useEffect(
        () => {

            /*
             * Do not call the API automatically until the user
             * provides the required Company / FY / Period filters.
             *
             * This avoids an unnecessary 400 response on page load.
             */

        },
        []
    );


    // ==============================================================
    // SEARCH
    // ==============================================================

    const handleSearch = () => {

        // ----------------------------------------------------------
        // Required filters
        // ----------------------------------------------------------

        if (
            !filters.company_id
        ) {

            setErrorMessage(
                "Please select Company."
            );

            return;

        }


        if (
            !filters.financial_year_id
        ) {

            setErrorMessage(
                "Please select Financial Year."
            );

            return;

        }


        if (
            !filters.financial_period_id
        ) {

            setErrorMessage(
                "Please select Financial Period."
            );

            return;

        }


        loadGeneralLedger(
            filters
        );

    };


    // ==============================================================
    // RESET
    // ==============================================================

    const handleReset = () => {

        const resetFilters = {

            company_id: null,

            financial_year_id: null,

            financial_period_id: null,

            account_id: null,

            date_from: "",

            date_to: "",

        };


        setFilters(
            resetFilters
        );


        setSelectedCompany(
            null
        );


        setSelectedFinancialYear(
            null
        );


        setSelectedFinancialPeriod(
            null
        );


        setSelectedAccount(
            null
        );


        setRows([]);


        setSummary({

            opening_balance: 0,

            total_debit: 0,

            total_credit: 0,

            closing_balance: 0,

        });


        setViewMode(
            "SUMMARY"
        );


        setErrorMessage(
            ""
        );

    };


    // ==============================================================
    // FORMAT AMOUNT
    // ==============================================================

    const formatAmount = (
        value
    ) => {

        const amount =
            Number(
                value || 0
            );


        return amount.toLocaleString(
            "en-IN",
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }
        );

    };


    // ==============================================================
    // SUMMARY CARDS
    // ==============================================================

    const summaryCards = useMemo(
        () => [

            {
                title:
                    "Opening Balance",

                value:
                    formatAmount(
                        summary.opening_balance
                    ),

            },

            {
                title:
                    "Total Debit",

                value:
                    formatAmount(
                        summary.total_debit
                    ),

            },

            {
                title:
                    "Total Credit",

                value:
                    formatAmount(
                        summary.total_credit
                    ),

            },

            {
                title:
                    "Closing Balance",

                value:
                    formatAmount(
                        summary.closing_balance
                    ),

            },

        ],
        [
            summary
        ]
    );


    // ==============================================================
    // RENDER
    // ==============================================================

    return (

        <div className="container-fluid">

            {/* ======================================================
                HEADER
            ====================================================== */}

            <FinancePageHeader
                title="General Ledger"
                subtitle="General Ledger Inquiry"
            />


            {/* ======================================================
                SUMMARY
            ====================================================== */}

            <FinanceSummaryCards
                cards={
                    summaryCards
                }
            />


            {/* ======================================================
                FILTER PANEL
            ====================================================== */}

            <FinanceSearchPanel
                loading={
                    loading
                }
                onSearch={
                    handleSearch
                }
                onReset={
                    handleReset
                }
            >

                {/* ==================================================
                    COMPANY
                ================================================== */}

                <div className="col-lg-3 col-md-6">

                    <label className="form-label">

                        Company

                    </label>


                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={
                            loadCompanies
                        }
                        isClearable
                        value={
                            selectedCompany
                        }
                        onChange={
                            option => {

                                setSelectedCompany(
                                    option
                                );


                                /*
                                 * Company changes invalidate
                                 * Account selection.
                                 */

                                setSelectedAccount(
                                    null
                                );


                                updateFilter(
                                    "company_id",
                                    option?.value || null
                                );


                                updateFilter(
                                    "account_id",
                                    null
                                );

                            }
                        }
                    />

                </div>


                {/* ==================================================
                    FINANCIAL YEAR
                ================================================== */}

                <div className="col-lg-3 col-md-6">

                    <label className="form-label">

                        Financial Year

                    </label>


                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={
                            loadFinancialYears
                        }
                        isClearable
                        value={
                            selectedFinancialYear
                        }
                        onChange={
                            option => {

                                setSelectedFinancialYear(
                                    option
                                );


                                /*
                                 * Financial Year changes
                                 * invalidate Financial Period.
                                 */

                                setSelectedFinancialPeriod(
                                    null
                                );


                                updateFilter(
                                    "financial_year_id",
                                    option?.value || null
                                );


                                updateFilter(
                                    "financial_period_id",
                                    null
                                );

                            }
                        }
                    />

                </div>


                {/* ==================================================
                    FINANCIAL PERIOD
                ================================================== */}

                <div className="col-lg-3 col-md-6">

                    <label className="form-label">

                        Financial Period

                    </label>


                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={
                            loadFinancialPeriods
                        }
                        isClearable
                        isDisabled={
                            !filters.financial_year_id
                        }
                        value={
                            selectedFinancialPeriod
                        }
                        onChange={
                            option => {

                                setSelectedFinancialPeriod(
                                    option
                                );


                                updateFilter(
                                    "financial_period_id",
                                    option?.value || null
                                );

                            }
                        }
                    />

                </div>


                {/* ==================================================
                    ACCOUNT
                ================================================== */}

                <div className="col-lg-3 col-md-6">

                    <label className="form-label">

                        Account

                    </label>


                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={
                            loadAccounts
                        }
                        isClearable
                        isDisabled={
                            !filters.company_id
                        }
                        value={
                            selectedAccount
                        }
                        onChange={
                            option => {

                                setSelectedAccount(
                                    option
                                );


                                updateFilter(
                                    "account_id",
                                    option?.value || null
                                );

                            }
                        }
                    />

                </div>


                {/* ==================================================
                    DATE FROM
                ================================================== */}

                <div className="col-lg-3 col-md-6">

                    <label className="form-label">

                        Date From

                    </label>


                    <input
                        type="date"
                        className="form-control"
                        value={
                            filters.date_from
                        }
                        onChange={
                            event =>
                                updateFilter(
                                    "date_from",
                                    event.target.value
                                )
                        }
                    />

                </div>


                {/* ==================================================
                    DATE TO
                ================================================== */}

                <div className="col-lg-3 col-md-6">

                    <label className="form-label">

                        Date To

                    </label>


                    <input
                        type="date"
                        className="form-control"
                        value={
                            filters.date_to
                        }
                        onChange={
                            event =>
                                updateFilter(
                                    "date_to",
                                    event.target.value
                                )
                        }
                    />

                </div>

            </FinanceSearchPanel>


            {/* ======================================================
                ERROR
            ====================================================== */}

            {
                errorMessage && (

                    <div className="alert alert-danger mt-3">

                        {errorMessage}

                    </div>

                )
            }


            {/* ======================================================
                GENERAL LEDGER SUMMARY
            ====================================================== */}

            {
                viewMode === "SUMMARY" && (

                    <div className="card mt-3">

                        <div className="card-header">

                            <strong>
                                General Ledger Summary
                            </strong>

                        </div>


                        <div className="card-body p-0">

                            <div className="table-responsive">

                                <table
                                    className={
                                        "table " +
                                        "table-hover " +
                                        "table-bordered " +
                                        "mb-0"
                                    }
                                >

                                    <thead>

                                        <tr>

                                            <th>
                                                Account Code
                                            </th>

                                            <th>
                                                Account Name
                                            </th>

                                            <th>
                                                Account Type
                                            </th>

                                            <th className="text-end">
                                                Opening
                                            </th>

                                            <th className="text-end">
                                                Debit
                                            </th>

                                            <th className="text-end">
                                                Credit
                                            </th>

                                            <th className="text-end">
                                                Closing
                                            </th>

                                        </tr>

                                    </thead>


                                    <tbody>

                                        {
                                            loading ? (

                                                <tr>

                                                    <td
                                                        colSpan="7"
                                                        className={
                                                            "text-center " +
                                                            "py-4"
                                                        }
                                                    >

                                                        Loading
                                                        General Ledger...

                                                    </td>

                                                </tr>

                                            ) : rows.length === 0 ? (

                                                <tr>

                                                    <td
                                                        colSpan="7"
                                                        className={
                                                            "text-center " +
                                                            "py-4"
                                                        }
                                                    >

                                                        No ledger balances
                                                        found.

                                                    </td>

                                                </tr>

                                            ) : (

                                                rows.map(
                                                    (
                                                        row,
                                                        index
                                                    ) => (

                                                        <tr
                                                            key={
                                                                row.account_id ||
                                                                index
                                                            }
                                                        >

                                                            <td>

                                                                {
                                                                    row.account_code ||
                                                                    "-"
                                                                }

                                                            </td>


                                                            <td>

                                                                {
                                                                    row.account_name ||
                                                                    "-"
                                                                }

                                                            </td>


                                                            <td>

                                                                {
                                                                    row.account_type ||
                                                                    "-"
                                                                }

                                                            </td>


                                                            <td
                                                                className="text-end"
                                                            >

                                                                {
                                                                    formatAmount(
                                                                        row.opening_balance
                                                                    )
                                                                }

                                                            </td>


                                                            <td
                                                                className="text-end"
                                                            >

                                                                {
                                                                    formatAmount(
                                                                        row.debit_total
                                                                    )
                                                                }

                                                            </td>


                                                            <td
                                                                className="text-end"
                                                            >

                                                                {
                                                                    formatAmount(
                                                                        row.credit_total
                                                                    )
                                                                }

                                                            </td>


                                                            <td
                                                                className="text-end"
                                                            >

                                                                {
                                                                    formatAmount(
                                                                        row.closing_balance
                                                                    )
                                                                }

                                                            </td>

                                                        </tr>

                                                    )
                                                )

                                            )
                                        }

                                    </tbody>

                                </table>

                            </div>

                        </div>

                    </div>

                )
            }


            {/* ======================================================
                ACCOUNT LEDGER
            ====================================================== */}

            {
                viewMode === "ACCOUNT" && (

                    <div className="card mt-3">

                        <div className="card-header">

                            <strong>

                                Account Ledger

                            </strong>


                            {
                                selectedAccount && (

                                    <span className="ms-2">

                                        —
                                        {
                                            selectedAccount.label
                                        }

                                    </span>

                                )
                            }

                        </div>


                        <div className="card-body p-0">

                            <div className="table-responsive">

                                <table
                                    className={
                                        "table " +
                                        "table-hover " +
                                        "table-bordered " +
                                        "mb-0"
                                    }
                                >

                                    <thead>

                                        <tr>

                                            <th>
                                                Date
                                            </th>

                                            <th>
                                                Journal No.
                                            </th>

                                            <th>
                                                Journal Type
                                            </th>

                                            <th>
                                                Description
                                            </th>

                                            <th className="text-end">
                                                Debit
                                            </th>

                                            <th className="text-end">
                                                Credit
                                            </th>

                                            <th className="text-end">
                                                Running Balance
                                            </th>

                                        </tr>

                                    </thead>


                                    <tbody>

                                        {
                                            loading ? (

                                                <tr>

                                                    <td
                                                        colSpan="7"
                                                        className={
                                                            "text-center " +
                                                            "py-4"
                                                        }
                                                    >

                                                        Loading Account
                                                        Ledger...

                                                    </td>

                                                </tr>

                                            ) : rows.length === 0 ? (

                                                <tr>

                                                    <td
                                                        colSpan="7"
                                                        className={
                                                            "text-center " +
                                                            "py-4"
                                                        }
                                                    >

                                                        No ledger
                                                        transactions found.

                                                    </td>

                                                </tr>

                                            ) : (

                                                rows.map(
                                                    (
                                                        row,
                                                        index
                                                    ) => (

                                                        <tr
                                                            key={
                                                                row.journal_line_id ||
                                                                index
                                                            }
                                                        >

                                                            <td>

                                                                {
                                                                    row.journal_date ||
                                                                    "-"
                                                                }

                                                            </td>


                                                            <td>

                                                                {
                                                                    row.journal_no ||
                                                                    "-"
                                                                }

                                                            </td>


                                                            <td>

                                                                {
                                                                    row.journal_type ||
                                                                    "-"
                                                                }

                                                            </td>


                                                            <td>

                                                                {
                                                                    row.description ||
                                                                    "-"
                                                                }

                                                            </td>


                                                            <td
                                                                className="text-end"
                                                            >

                                                                {
                                                                    formatAmount(
                                                                        row.debit
                                                                    )
                                                                }

                                                            </td>


                                                            <td
                                                                className="text-end"
                                                            >

                                                                {
                                                                    formatAmount(
                                                                        row.credit
                                                                    )
                                                                }

                                                            </td>


                                                            <td
                                                                className="text-end"
                                                            >

                                                                {
                                                                    formatAmount(
                                                                        row.running_balance
                                                                    )
                                                                }

                                                            </td>

                                                        </tr>

                                                    )
                                                )

                                            )
                                        }

                                    </tbody>

                                </table>

                            </div>

                        </div>

                    </div>

                )
            }

        </div>

    );

}